/**
 * The Letterboxd import matching ladder (PLAN §5). Each rung is tried in
 * order and the first hit wins:
 *
 * 1. Letterboxd URI slug vs `links.letterboxdSlug` — exact.
 * 2. `normalize(title)` vs `normalize(match.normalizedTitle)`, exact year.
 * 3. Same, year within ±1 (release-date-vs-diary-date drift, timezone edges).
 * 4. Same as 3 but against `match.aliases` instead of the primary title.
 * 5. Dice bigram coefficient ≥ 0.88 against title or any alias — auto-accept.
 * 6. 0.70–0.88 — sent to `MatchReview` for one-click confirmation.
 * 7. Below 0.70 — unmatched, listed for transparency.
 *
 * TV entries (`availability.letterboxdTrackable === false`) never enter the
 * candidate pool — Letterboxd can't represent them, so matching against them
 * would only ever be a coincidence of title text.
 */

import type { Entry, EntryId } from "@schema/catalog.ts";

const AUTO_ACCEPT_THRESHOLD = 0.88;
const REVIEW_THRESHOLD = 0.7;
/** How many runner-up candidates `MatchReview` shows alongside the top guess. */
const REVIEW_ALTERNATE_LIMIT = 2;

const LEADING_ARTICLE_RE = /^(the|a|an)\s+/;

/** Roman-numeral sequel markers → digits. Single-letter tokens (i, v, x, ...)
 * are deliberately excluded: those collide with real words/initials far more
 * often than multi-letter ones ("ii", "iii", "xii") do. */
const ROMAN_NUMERALS: Record<string, string> = {
  ii: "2",
  iii: "3",
  iv: "4",
  vi: "6",
  vii: "7",
  viii: "8",
  ix: "9",
  xi: "11",
  xii: "12",
  xiii: "13",
  xiv: "14",
  xv: "15",
  xvi: "16",
  xvii: "17",
  xviii: "18",
  xix: "19",
  xx: "20",
};

/**
 * Lowercases, strips diacritics and punctuation, drops a leading article,
 * and converts roman-numeral sequel markers to digits. Idempotent, so it's
 * safe to run again on catalog `match.normalizedTitle` values, which are
 * already lowercased/punctuation-stripped but don't do the article/roman step.
 */
export function normalize(title: string): string {
  const stripped = title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
  const withoutArticle = stripped.replace(LEADING_ARTICLE_RE, "") || stripped;
  return withoutArticle
    .split(" ")
    .map((word) => ROMAN_NUMERALS[word] ?? word)
    .join(" ");
}

function bigrams(value: string): string[] {
  const s = value.replace(/\s+/g, "");
  const grams: string[] = [];
  for (let i = 0; i < s.length - 1; i++) grams.push(s.slice(i, i + 2));
  return grams;
}

/**
 * Sørensen–Dice coefficient over character bigrams, 0–1. Multiset-aware (a
 * repeated bigram in `a` can only match a repeated bigram in `b` once each),
 * which plain-set intersection gets wrong for short/repetitive titles.
 */
export function diceCoefficient(a: string, b: string): number {
  if (a === b) return 1;
  const bigramsA = bigrams(a);
  const bigramsB = bigrams(b);
  if (bigramsA.length === 0 || bigramsB.length === 0) return 0;

  const counts = new Map<string, number>();
  for (const g of bigramsA) counts.set(g, (counts.get(g) ?? 0) + 1);

  let matches = 0;
  for (const g of bigramsB) {
    const count = counts.get(g) ?? 0;
    if (count > 0) {
      matches++;
      counts.set(g, count - 1);
    }
  }
  return (2 * matches) / (bigramsA.length + bigramsB.length);
}

/** One parsed Letterboxd CSV row, reduced to what the ladder needs. */
export interface MatchInput {
  title: string;
  /** `null` when the CSV row has no year (rare, but the export format allows it). */
  year: number | null;
  /** The film slug parsed from the row's "Letterboxd URI" column, if present. */
  slug: string | null;
}

export type MatchTier =
  | "slug"
  | "title-year"
  | "title-year-adjacent"
  | "alias-year-adjacent"
  | "fuzzy-auto"
  | "fuzzy-review";

export interface MatchHit {
  entryId: EntryId;
  tier: MatchTier;
  /** 1 for exact-tier hits; the Dice score for the two fuzzy tiers. */
  score: number;
}

export type MatchStatus = "matched" | "review" | "unmatched";

export interface MatchOutcome {
  status: MatchStatus;
  /** The best candidate; `null` only when `status === "unmatched"`. */
  hit: MatchHit | null;
  /** Runner-up candidates for `MatchReview`; populated only when `status === "review"`. */
  alternates: MatchHit[];
}

/** Entries Letterboxd could plausibly represent — TV is excluded up front. */
export function matchCandidates(entries: Entry[]): Entry[] {
  return entries.filter((e) => e.availability.letterboxdTrackable);
}

function bestDiceForEntry(normalizedTitle: string, entry: Entry): number {
  const names = [entry.match.normalizedTitle, ...entry.match.aliases].map(normalize);
  return Math.max(...names.map((name) => diceCoefficient(normalizedTitle, name)));
}

const asMatched = (hit: MatchHit): MatchOutcome => ({ status: "matched", hit, alternates: [] });

/** Runs one Letterboxd row through the full ladder against the catalog. */
export function findMatch(row: MatchInput, entries: Entry[]): MatchOutcome {
  const candidates = matchCandidates(entries);
  const title = normalize(row.title);

  if (row.slug) {
    const bySlug = candidates.find((e) => e.links.letterboxdSlug === row.slug);
    if (bySlug) return asMatched({ entryId: bySlug.id, tier: "slug", score: 1 });
  }

  for (const entry of candidates) {
    if (normalize(entry.match.normalizedTitle) === title && entry.year === row.year) {
      return asMatched({ entryId: entry.id, tier: "title-year", score: 1 });
    }
  }

  if (row.year !== null) {
    for (const entry of candidates) {
      if (
        normalize(entry.match.normalizedTitle) === title &&
        Math.abs(entry.year - row.year) <= 1
      ) {
        return asMatched({ entryId: entry.id, tier: "title-year-adjacent", score: 1 });
      }
    }

    for (const entry of candidates) {
      const aliasHit = entry.match.aliases.some((alias) => normalize(alias) === title);
      if (aliasHit && Math.abs(entry.year - row.year) <= 1) {
        return asMatched({ entryId: entry.id, tier: "alias-year-adjacent", score: 1 });
      }
    }
  }

  const scored = candidates
    .map((entry) => ({ entryId: entry.id, score: bestDiceForEntry(title, entry) }))
    .sort((a, b) => b.score - a.score);

  const best = scored[0];
  if (!best || best.score < REVIEW_THRESHOLD) {
    return { status: "unmatched", hit: null, alternates: [] };
  }

  if (best.score >= AUTO_ACCEPT_THRESHOLD) {
    return asMatched({ entryId: best.entryId, tier: "fuzzy-auto", score: best.score });
  }

  const alternates = scored
    .slice(1, 1 + REVIEW_ALTERNATE_LIMIT)
    .filter((c) => c.score >= REVIEW_THRESHOLD)
    .map((c): MatchHit => ({ entryId: c.entryId, tier: "fuzzy-review", score: c.score }));

  return {
    status: "review",
    hit: { entryId: best.entryId, tier: "fuzzy-review", score: best.score },
    alternates,
  };
}

export function matchAll(rows: MatchInput[], entries: Entry[]): MatchOutcome[] {
  return rows.map((row) => findMatch(row, entries));
}
